import React, { useEffect, useState } from 'react'
import { aiApi, shoppingApi } from '../lib/api.js'

const AISLE_LABELS = {
  produce: '🥬 Fruits & légumes',
  meat: '🥩 Boucherie',
  fish: '🐟 Poissonnerie',
  dairy: '🧀 Crèmerie',
  bakery: '🥖 Boulangerie',
  dry: '🍝 Épicerie sèche',
  frozen: '🧊 Surgelés',
  beverages: '🥤 Boissons',
  household: '🧽 Maison',
}

const STORE_BADGE_COLORS = {
  delhaize: 'bg-red-100 text-red-700',
  colruyt: 'bg-blue-100 text-blue-700',
  lidl: 'bg-yellow-100 text-yellow-800',
  carrefour: 'bg-red-100 text-red-700',
  'albert-heijn': 'bg-blue-100 text-blue-700',
  aldi: 'bg-sky-100 text-sky-700',
}

/**
 * ShoppingListDrawer — generates an aggregated shopping list from the week's
 * planned meals (LLM-powered ingredient extraction) and overlays Promos badges
 * to bridge with the Promos page.
 */
export function ShoppingListDrawer({ weekStart, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checked, setChecked] = useState(new Set())
  const [saving, setSaving] = useState(false)
  const [savedListId, setSavedListId] = useState(null)

  async function generate() {
    setLoading(true)
    setError(null)
    try {
      const res = await aiApi.shoppingList({ weekStart })
      setData(res)
    } catch (e) {
      setError("Impossible de générer la liste. Réessaie dans un instant.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    generate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart])

  function toggleItem(idx) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  async function handleSave() {
    if (!data?.items?.length) return
    setSaving(true)
    try {
      const list = await shoppingApi.create({ name: `Semaine du ${weekStart}` })
      await shoppingApi.bulkAddItems(
        list.id,
        data.items.map((it) => ({ name: it.name, quantity: it.quantity, unit: it.unit })),
      )
      setSavedListId(list.id)
    } catch (e) {
      setError("Impossible de sauver la liste.")
    } finally {
      setSaving(false)
    }
  }

  // Group by aisle
  const grouped = (data?.items ?? []).reduce((acc, item, idx) => {
    const a = item.aisle ?? 'dry'
    if (!acc[a]) acc[a] = []
    acc[a].push({ ...item, _idx: idx })
    return acc
  }, {})

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛒</span>
            <div>
              <h2 className="text-base font-bold text-gray-900">Liste de courses</h2>
              <p className="text-xs text-gray-400">
                {data ? `${data.items.length} ingrédients · ${data.dishCount} plats` : weekStart}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1" aria-label="Fermer">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-400">L'IA extrait les ingrédients de tes plats…</p>
            </div>
          )}

          {error && (
            <div className="py-8 text-center">
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <button onClick={generate} className="text-xs text-emerald-600 underline hover:no-underline">
                Réessayer
              </button>
            </div>
          )}

          {!loading && !error && data && data.items.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-4xl mb-3">📭</p>
              <p className="text-sm text-gray-500">
                Pas de plats planifiés cette semaine — la liste est vide.
              </p>
            </div>
          )}

          {!loading && data && data.items.length > 0 && (
            <div className="space-y-5">
              {Object.entries(grouped).map(([aisle, items]) => (
                <section key={aisle}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {AISLE_LABELS[aisle] ?? aisle}
                  </h3>
                  <ul className="space-y-1.5">
                    {items.map((item) => {
                      const isChecked = checked.has(item._idx)
                      return (
                        <li key={item._idx}>
                          <button
                            onClick={() => toggleItem(item._idx)}
                            className="w-full flex items-start gap-3 text-left border border-gray-100 rounded-lg px-3 py-2.5 hover:border-emerald-200 transition-colors"
                          >
                            <span
                              className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center text-xs ${
                                isChecked
                                  ? 'bg-emerald-500 border-emerald-500 text-white'
                                  : 'border-gray-300'
                              }`}
                            >
                              {isChecked && '✓'}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${isChecked ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                {item.name}
                                <span className="text-gray-400 font-normal ml-2 text-xs">
                                  {item.quantity} {item.unit}
                                </span>
                              </p>
                              {item.promos?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {item.promos.map((promo, i) => (
                                    <span
                                      key={i}
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                        STORE_BADGE_COLORS[promo.store?.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      💰 {promo.store} {promo.promoType}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {data && data.items.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex gap-2">
            <button
              onClick={generate}
              disabled={loading}
              className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg disabled:opacity-50"
            >
              ↺ Régénérer
            </button>
            <button
              onClick={handleSave}
              disabled={saving || savedListId}
              className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {savedListId ? '✓ Sauvée dans Courses' : saving ? 'Sauvegarde…' : '💾 Sauver dans Courses'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
